/**
 * SOLOMON VOICE PANEL
 * Phase 1 / Day 6: Voice Controls
 *
 * Contains:
 * - Speaker toggle (voice output on/off)
 * - Voice selector (pick from ElevenLabs voices)
 * - Stop speaking button (when active)
 *
 * Day 6 fixes:
 * - Voices auto-load when voice is enabled (not just when picker opens)
 * - First voice auto-selected if none chosen yet
 * - Selected voice name shown immediately (no "Pick voice" flash)
 */

import { useState, useEffect, useRef } from 'react';

export default function VoicePanel({
  voiceEnabled,
  selectedVoiceId,
  isSpeaking,
  fetchVoices,
  onToggleVoice,
  onSelectVoice,
  onStopSpeaking,
}) {
  const [voices,      setVoices]      = useState([]);
  const [showPicker,  setShowPicker]  = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const fetchedRef = useRef(false);   // prevent duplicate fetches

  // Auto-load voices whenever voice is enabled
  useEffect(() => {
    if (!voiceEnabled) return;
    if (fetchedRef.current && voices.length > 0) return;  // already loaded

    fetchedRef.current = true;
    setLoadingList(true);

    fetchVoices().then((list) => {
      setVoices(list);
      setLoadingList(false);

      // Auto-select first voice if nothing is chosen yet
      if (!selectedVoiceId && list.length > 0) {
        onSelectVoice(list[0].id);
      }
    });
  }, [voiceEnabled]);   // intentionally only re-runs when voiceEnabled changes

  // Also load when picker opens (if for some reason voices are empty)
  useEffect(() => {
    if (!showPicker || voices.length > 0) return;
    setLoadingList(true);
    fetchVoices().then((list) => {
      setVoices(list);
      setLoadingList(false);
      if (!selectedVoiceId && list.length > 0) {
        onSelectVoice(list[0].id);
      }
    });
  }, [showPicker]);   // intentionally minimal deps — just needs to fire on open

  // Find the selected voice for display — works even before picker opens
  const selectedVoice = voices.find((v) => v.id === selectedVoiceId);

  // Display name: show voice name if loaded, show short ID as fallback,
  // or "Loading…" while fetching
  const voiceDisplayName = selectedVoice
    ? selectedVoice.name
    : loadingList
      ? 'Loading…'
      : selectedVoiceId
        ? '…'          // ID is saved but voices haven't loaded yet
        : 'Pick voice';

  return (
    <div className="voice-panel">

      {/* Stop speaking button — only when audio is playing */}
      {isSpeaking && (
        <button
          className="voice-btn voice-btn--stop"
          onClick={onStopSpeaking}
          title="Stop speaking"
          aria-label="Stop Solomon from speaking"
        >
          <StopIcon />
          <span className="voice-btn-label">Stop</span>
        </button>
      )}

      {/* Speaker toggle */}
      <button
        className={`voice-btn voice-btn--speaker ${voiceEnabled ? 'voice-btn--on' : ''}`}
        onClick={onToggleVoice}
        title={voiceEnabled ? 'Turn off voice' : 'Turn on voice'}
        aria-label={voiceEnabled ? 'Disable voice output' : 'Enable voice output'}
      >
        {voiceEnabled ? <SpeakerOnIcon /> : <SpeakerOffIcon />}
      </button>

      {/* Voice selector — only visible when voice is enabled */}
      {voiceEnabled && (
        <div className="voice-selector-wrapper">
          <button
            className="voice-selector-btn"
            onClick={() => setShowPicker((p) => !p)}
            title="Select voice"
          >
            <span className="voice-selector-name">{voiceDisplayName}</span>
            <ChevronIcon />
          </button>

          {showPicker && (
            <div className="voice-picker-dropdown">
              <div className="voice-picker-header">Select Solomon's Voice</div>

              {loadingList && (
                <div className="voice-picker-loading">Loading voices…</div>
              )}

              {!loadingList && voices.length === 0 && (
                <div className="voice-picker-empty">No voices found. Check your ElevenLabs account.</div>
              )}

              {voices.map((voice) => (
                <button
                  key={voice.id}
                  className={`voice-picker-option ${voice.id === selectedVoiceId ? 'voice-picker-option--selected' : ''}`}
                  onClick={() => {
                    onSelectVoice(voice.id);
                    setShowPicker(false);
                  }}
                >
                  <div className="voice-option-name">{voice.name}</div>
                  <div className="voice-option-meta">
                    {[voice.gender, voice.accent, voice.useCase].filter(Boolean).join(' · ')}
                  </div>
                  {voice.id === selectedVoiceId && (
                    <span className="voice-option-check">✓</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// ICONS
// ============================================================

function SpeakerOnIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    </svg>
  );
}

function SpeakerOffIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <line x1="23" y1="9" x2="17" y2="15" />
      <line x1="17" y1="9" x2="23" y2="15" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
      <rect x="3" y="3" width="18" height="18" rx="2" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
